/**
 * Date-range presets for Fleet P&L Reports.
 * Windows only — does not recompute P&L; engine owns attribution.
 * All dates as UTC calendar days (YYYY-MM-DD) for API startDate/endDate.
 */

function pad(n) {
  return String(n).padStart(2, '0');
}

export function ymdUTC(d) {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function startOfUTCDay(d = new Date()) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function endOfMonthUTC(year, monthIndex) {
  // day 0 of next month = last day of monthIndex
  return new Date(Date.UTC(year, monthIndex + 1, 0));
}

function startOfQuarterUTC(d) {
  const q = Math.floor(d.getUTCMonth() / 3);
  return new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1));
}

export const RANGE_PRESETS = [
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_quarter', label: 'Last Quarter' },
  { id: 'custom', label: 'Custom' },
];

/**
 * @returns {{ startDate: string, endDate: string, label: string }}
 */
export function resolveRange(presetId, customStart, customEnd, now = new Date()) {
  const today = startOfUTCDay(now);
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();

  switch (presetId) {
    case 'this_week': {
      // Week = Mon–Sun UTC containing today
      const dow = today.getUTCDay(); // 0=Sun
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const start = new Date(today);
      start.setUTCDate(today.getUTCDate() + mondayOffset);
      return {
        startDate: ymdUTC(start),
        endDate: ymdUTC(today),
        label: 'This Week',
      };
    }
    case 'this_month': {
      const start = new Date(Date.UTC(y, m, 1));
      return {
        startDate: ymdUTC(start),
        endDate: ymdUTC(today),
        label: 'This Month',
      };
    }
    case 'this_quarter': {
      const start = startOfQuarterUTC(today);
      return {
        startDate: ymdUTC(start),
        endDate: ymdUTC(today),
        label: 'This Quarter',
      };
    }
    case 'last_month': {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      const start = new Date(Date.UTC(ly, lm, 1));
      const end = endOfMonthUTC(ly, lm);
      return {
        startDate: ymdUTC(start),
        endDate: ymdUTC(end),
        label: `${start.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })} ${ly}`,
      };
    }
    case 'last_quarter': {
      const q = Math.floor(m / 3); // 0–3 current
      const prevQ = q === 0 ? 3 : q - 1;
      const py = q === 0 ? y - 1 : y;
      const start = new Date(Date.UTC(py, prevQ * 3, 1));
      const end = endOfMonthUTC(py, prevQ * 3 + 2);
      return {
        startDate: ymdUTC(start),
        endDate: ymdUTC(end),
        label: `Q${prevQ + 1} ${py}`,
      };
    }
    case 'custom':
    default: {
      const startDate = customStart || ymdUTC(new Date(Date.UTC(y, m, 1)));
      const endDate = customEnd || ymdUTC(today);
      return {
        startDate,
        endDate,
        label: `${startDate} → ${endDate}`,
      };
    }
  }
}

/**
 * Last N complete-or-current periods ending at `endDate` (inclusive).
 * grain: 'month' | 'week'
 */
export function lastNPeriods(endDateYmd, n, grain = 'month') {
  const end = new Date(`${endDateYmd}T00:00:00Z`);
  const periods = [];
  if (grain === 'week') {
    let cursor = startOfUTCDay(end);
    for (let i = 0; i < n; i += 1) {
      const dow = cursor.getUTCDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const weekStart = new Date(cursor);
      weekStart.setUTCDate(cursor.getUTCDate() + mondayOffset);
      const weekEnd = new Date(weekStart);
      weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
      const clippedEnd = weekEnd > end ? end : weekEnd;
      periods.unshift({
        startDate: ymdUTC(weekStart),
        endDate: ymdUTC(clippedEnd),
        label: `${ymdUTC(weekStart)}`,
      });
      cursor = new Date(weekStart);
      cursor.setUTCDate(weekStart.getUTCDate() - 1);
    }
    return periods;
  }

  // months
  let y = end.getUTCFullYear();
  let m = end.getUTCMonth();
  for (let i = 0; i < n; i += 1) {
    const start = new Date(Date.UTC(y, m, 1));
    const monthEnd = endOfMonthUTC(y, m);
    const clippedEnd = i === 0 && end < monthEnd ? end : monthEnd;
    periods.unshift({
      startDate: ymdUTC(start),
      endDate: ymdUTC(clippedEnd),
      label: start.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
    });
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  return periods;
}

export function toApiDates(startDate, endDate) {
  return {
    startDate: `${startDate}T00:00:00.000Z`,
    endDate: `${endDate}T23:59:59.999Z`,
  };
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const KNOWN_TABS = ['summary', 'unit', 'lanes', 'reports', 'ifta'];

/**
 * Query from Llama "Open Fleet P&L" and direct report links.
 * tab=reports&unit=2226&startDate=2026-09-14&endDate=2026-09-19
 */
export function parseFleetIntelligenceQuery(search = '') {
  const raw = String(search || '').replace(/^\?/, '');
  const params = new URLSearchParams(raw);
  const tab = String(params.get('tab') || '').trim().toLowerCase();
  const ymd = (v) => (YMD.test(String(v || '').trim()) ? String(v).trim() : null);
  return {
    view: KNOWN_TABS.includes(tab) ? tab : null,
    startDate: ymd(params.get('startDate')),
    endDate: ymd(params.get('endDate')),
    unit: String(params.get('unit') || params.get('unitNumber') || '').trim() || null,
  };
}

/** Apply uses typed dates, or the currently displayed range — never silent month-to-date. */
export function resolveAppliedCustomRange(customStart, customEnd, fallback = {}, now = new Date()) {
  const start = customStart || fallback.startDate || null;
  const end = customEnd || fallback.endDate || null;
  return resolveRange('custom', start, end, now);
}

export function reportsRangeFromQuery(query = {}, now = new Date()) {
  if (query.startDate && query.endDate) {
    const range = resolveRange('custom', query.startDate, query.endDate, now);
    return {
      preset: 'custom',
      range,
      customStart: query.startDate,
      customEnd: query.endDate,
    };
  }
  return {
    preset: 'last_month',
    range: resolveRange('last_month', null, null, now),
    customStart: '',
    customEnd: '',
  };
}
